'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';

import { BadgeCheck, Copy, Moon, Share2, Sun } from 'lucide-react';
import { toast } from 'sonner';

import { Heading } from '@/components/heading';
import { useThemeController } from '@/components/theme/theme-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

type AnonymousUserFooterProps = {
  daysToExpiry: number;
};

export function AnonymousUserFooter({ daysToExpiry }: AnonymousUserFooterProps) {
  return (
    <div className="sticky bottom-10 mt-5 px-[8%]">
      <Card className="shadow-lg/30">
        <CardContent className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:gap-12 sm:text-left">
          <div>
            <Heading level={4} size={5}>
              Your status page is live!
            </Heading>
            <p className="text-muted-foreground text-sm">
              But it will expire in{' '}
              <b>
                {daysToExpiry} {daysToExpiry === 1 ? 'day' : 'days'}
              </b>
              . Create a free account to claim it.
            </p>
          </div>
          <Button asChild>
            <Link href="/auth/sign-up" className="flex items-center gap-2">
              <BadgeCheck className="size-4" /> Claim My Page
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

type FloatingControlsProps = {
  align?: 'top-right' | 'bottom-right';
};

export function AnonymousStatusFloatingControls({
  align = 'top-right',
}: FloatingControlsProps = {}) {
  const { theme, setTheme } = useThemeController();
  const [hasCopied, setHasCopied] = useState(false);

  useEffect(() => {
    if (!hasCopied) return;

    const timeout = window.setTimeout(() => setHasCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [hasCopied]);

  const handleCopyLink = useCallback(async () => {
    const url = window.location.href;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setHasCopied(true);
      toast.success('Link copied to clipboard');
    } catch (error) {
      console.error('Failed to copy status page link', error);
      toast.error('Could not copy link. Try copying it manually.');
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
  }, [setTheme, theme]);

  const shareTooltipLabel = hasCopied ? 'Link copied!' : 'Copy status page link';
  const nextThemeLabel = theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme';

  const positionClasses = align === 'bottom-right' ? 'bottom-6 right-5' : 'right-5 top-5';

  return (
    <div
      className={`pointer-events-none fixed ${positionClasses} z-40 flex flex-col items-end gap-2`}
    >
      <div className="border-border bg-background/95 pointer-events-auto rounded-full border p-1.5 shadow-lg backdrop-blur">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleCopyLink}
                aria-label={shareTooltipLabel}
              >
                {hasCopied ? <Copy className="size-4" /> : <Share2 className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="whitespace-nowrap">
              {shareTooltipLabel}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                aria-label={nextThemeLabel}
              >
                {theme === 'light' ? <Moon className="size-4" /> : <Sun className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="whitespace-nowrap">
              {nextThemeLabel}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
